import PagedSearchTable, {PagedTableFunctions} from '../../components/PagedSearchTable'
import React, {useContext, useRef, useState} from 'react'
import Dialog from '../../components/Dialog'
import {EditRow, EditTable, focusFirstInput} from '../../components/Fields'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import LimeProductRequest from '../../controllers/LimeProductRequest'
import LimeProductType from '../../controllers/LimeProductType'
import LimingRegionResponse from '../../controllers/LimingRegionResponse'
import {useValidation} from '../../validation'
import {buildSetter} from '../../immutableState'
import Input from '../../components/Input'
import NumberNullable from '../../components/NumberNullable'
import SelectString from '../../components/SelectString'
import {showSuccessOrFailed} from '../../Snacks'
import {roundTo} from '../program/ViewBlock'
import {FieldStatus, getFieldConfig} from "./GetFieldConfig";

const emptyProduct: LimeProductRequest = {
    id: 0,
    name: '',
    type: LimeProductType.Other,
    regionId: null,
    registrationNumber: null,
    supplier: null,
    pricePerTon: null,
    location: null,
    caPercent: 0,
    caCO3Percent: 0,
    caOPercent: 0,
    caSO4Percent: 0,
    mgPercent: 0,
    mgCO3Percent: 0,
    mgOPercent: 0,
    ccePercent: 0,
    sPercent: 0,
    pPercent: 0,
    kPercent: 0,
    ph: null,
    caMgRatio: null,
    fineness: null,
    active: true
}

function toForm(item: LimeProductResponse): LimeProductRequest {
    return {
        id: item.id,
        name: item.name,
        type: item.type,
        regionId: item.regionId,
        registrationNumber: item.registrationNumber,
        supplier: item.supplier,
        pricePerTon: item.pricePerTon,
        location: item.location,
        caPercent: item.caPercent,
        caCO3Percent: item.caCO3Percent,
        caOPercent: item.caOPercent,
        caSO4Percent: item.caSO4Percent,
        mgPercent: item.mgPercent,
        mgCO3Percent: item.mgCO3Percent,
        mgOPercent: item.mgOPercent,
        ccePercent: item.ccePercent,
        sPercent: item.sPercent,
        pPercent: item.pPercent,
        kPercent: item.kPercent,
        ph: item.ph,
        caMgRatio: item.caMgRatio,
        fineness: item.fineness,
        active: item.active
    }
}

const productTypes = Object.values(LimeProductType).map(t => ({ id: t, name: t }))

type ChemicalFieldKey = keyof ReturnType<typeof getFieldConfig>

const chemicalFields: { key: ChemicalFieldKey, label: string, tableHeader?: string, percent: boolean }[] = [
    { key: 'caPercent', label: '% Ca', percent: true },
    { key: 'caCO3Percent', label: '% CaCO3', percent: true },
    { key: 'caOPercent', label: '% CaO', percent: true },
    { key: 'caSO4Percent', label: '% CaSO4', percent: true },
    { key: 'mgPercent', label: '% Mg', percent: true },
    { key: 'mgCO3Percent', label: '% MgCO3', percent: true },
    { key: 'mgOPercent', label: '% MgO', percent: true },
    { key: 'ccePercent', label: '% CCE', percent: true },
    { key: 'sPercent', label: '% S', percent: true },
    { key: 'pPercent', label: '% P', percent: true },
    { key: 'kPercent', label: '% K', percent: true },
    { key: 'ph', label: 'pH', percent: false },
    { key: 'caMgRatio', label: 'Ca:Mg Ratio', tableHeader: 'Ca:Mg', percent: false },
    { key: 'fineness', label: 'Fineness', percent: false },
]

const displayFieldRounded = (item: LimeProductResponse, field: ChemicalFieldKey, value: number | null, percent: boolean, decimals: number = 3): string => {
    const fields = getFieldConfig(item.type)
    if (fields[field] === FieldStatus.Hidden) return '-'
    if (value === null) return '-'
    const display = percent ? value * 100 : value
    return roundTo(display, decimals).toString()
}

const LimingProducts: React.FC = () => {
    const context = useContext(AppContext)
    const pagedTableRef = useRef<PagedTableFunctions<LimeProductResponse>>()
    const [show, setShow] = useState(false)
    const [upsertData, setUpsertData] = useState<LimeProductRequest>(emptyProduct)
    const setData = buildSetter(upsertData, setUpsertData)
    const [regions, setRegions] = useState<LimingRegionResponse[]>([])

    React.useEffect(() => {
        LimeController.getRegions().then(setRegions)
    }, [])

    const fieldConfig = getFieldConfig(upsertData.type)

    function showPopup(preset: LimeProductRequest) {
        setUpsertData(preset)
        setShow(true)
    }

    function upsertCallback(data: LimeProductRequest) {
        if (!validation.validate()) { return }

        showSuccessOrFailed(
            context,
            LimeController.productUpsert(data),
            data.id === 0 ? context.word('product_added') : context.word('product_updated')
        ).then(() => {
            pagedTableRef.current?.refresh()
            setShow(false)
        })
    }

    function setActive(id: number, state: boolean) {
        showSuccessOrFailed(context, LimeController.productToggleActive({
            id,
            active: state
        })).then(() => {
            pagedTableRef.current?.refresh()
        })
    }

    const strikeOut = (product: LimeProductResponse): string => {
        return !product.active ? 'text-red-500 italic line-through' : ''
    }

    const chemicalRules: Record<string, () => boolean> = {}
    chemicalFields.forEach(f => {
        chemicalRules[f.key] = () => {
            if (fieldConfig[f.key] !== FieldStatus.Required) return true
            const value = upsertData[f.key as keyof LimeProductRequest] as number | null
            return value !== null && value > 0
        }
    })

    const validation = useValidation<Record<string, () => boolean>>({
        nameRequired: () => upsertData.name.length > 0,
        ...chemicalRules,
    })

    return (
        <>
            <Dialog
                mounted={focusFirstInput}
                title={upsertData.id === 0 ? context.word('add_product') : context.word('edit_product')}
                show={show}
                setShow={setShow}
                body={
                    <EditTable
                        save={() => upsertCallback(upsertData)}
                        discard={() => setShow(false)}
                        saveWord={upsertData.id === 0 ? 'insert' : 'update'}
                    >
                        {EditRow(
                            context.word('name'),
                            <Input value={upsertData.name} change={v => setData({ name: v })} />,
                            validation.rules.nameRequired,
                            context.word('name_required')
                        )}
                        {EditRow(
                            context.word('type'),
                            <SelectString
                                options={productTypes}
                                textFunc={t => t.name}
                                valueFunc={t => t.id}
                                value={upsertData.type}
                                onChange={v => setData({ type: v as LimeProductType })}
                            />
                        )}
                        {EditRow(
                            context.word('region'),
                            <select
                                className="input w-full"
                                value={upsertData.regionId ?? ''}
                                onChange={e => setData({ regionId: e.target.value ? parseInt(e.target.value) : null })}
                            >
                                <option value="">{context.word('all_regions')}</option>
                                {regions.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                        )}
                        {EditRow(
                            context.word('registration_number'),
                            <Input value={upsertData.registrationNumber ?? ''} change={v => setData({ registrationNumber: v || null })} />
                        )}
                        {EditRow(
                            context.word('supplier'),
                            <Input value={upsertData.supplier ?? ''} change={v => setData({ supplier: v || null })} />
                        )}
                        {EditRow(
                            context.word('price_per_ton'),
                            <NumberNullable value={upsertData.pricePerTon} change={v => setData({ pricePerTon: v })} />
                        )}
                        {EditRow(
                            context.word('location'),
                            <Input value={upsertData.location ?? ''} change={v => setData({ location: v || null })} />
                        )}

                        {chemicalFields.map(f => {
                            if (fieldConfig[f.key] === FieldStatus.Hidden) return null
                            const stored = upsertData[f.key as keyof LimeProductRequest] as number | null
                            const displayValue = f.percent && stored !== null ? stored * 100 : stored
                            const label = context.word(f.key)
                            return <React.Fragment key={f.key}>
                                {EditRow(
                                    label,
                                    <NumberNullable value={displayValue} change={v => {
                                        const toStore = f.percent && v !== null ? v / 100 : v
                                        setData({ [f.key]: toStore } as Partial<LimeProductRequest>)
                                    }} />,
                                    validation.rules[f.key],
                                    context.word('required')
                                )}
                            </React.Fragment>
                        })}
                    </EditTable>
                }
            />

            <div className="btn bg-primary m-2" onClick={() => showPopup(emptyProduct)}>
                {context.word('new_product')}
            </div>

            <PagedSearchTable<LimeProductResponse>
                componentRef={pagedTableRef}
                call={LimeController.productsPaged}
                columns={[
                    { header: context.word('name'), row: item => <div className={strikeOut(item)}>{item.name}</div> },
                    { header: context.word('type'), row: item => <div className={strikeOut(item)}>{item.type}</div> },
                    { header: context.word('region'), row: item => <div className={strikeOut(item)}>{item.regionName ?? context.word('all_regions')}</div> },
                    ...chemicalFields.map(f => ({
                        header: context.word(f.key),
                        row: (item: LimeProductResponse) => (
                            <div className={strikeOut(item)}>
                                {displayFieldRounded(item, f.key, item[f.key as keyof LimeProductResponse] as number | null, f.percent)}
                            </div>
                        )
                    })),
                    {
                        header: context.word('action'),
                        row: item => (
                            <div className="flex gap-2">
                                <span
                                    className="underline cursor-pointer text-primary-600"
                                    onClick={() => showPopup(toForm(item))}
                                >
                                    {context.word('edit')}
                                </span>
                                <span
                                    className="underline cursor-pointer"
                                    onClick={() => setActive(item.id, !item.active)}
                                >
                                    {item.active ? context.word('deactivate') : context.word('activate')}
                                </span>
                            </div>
                        )
                    }
                ]}
                keyExtractor={i => i.id}
            />
        </>
    )
}

export default LimingProducts
